import { Search, SlidersHorizontal } from 'lucide-react'
import { useTranslations } from 'use-intl'
import { LocaleSwitcher } from '~/components/common/locale-switcher'
import { LocalizedLink } from '~/components/common/localized-link'
import { ModeToggle } from '~/components/common/mode-toggle'
import { MobilePublicNavMenu, PublicHeaderActions, PublicNavLinks } from '~/components/layout/public-header-nav'
import { GlobalSearchBar } from '../search/GlobalSearchBar'

interface MapHeaderProps {
  onBack?: () => void
  onSearch?: (query: string) => void
  onSearchClick?: () => void
  onFilterClick?: () => void
  activeFilterCount?: number
}

export function MapHeader({ onSearchClick, onFilterClick, activeFilterCount = 0 }: Readonly<MapHeaderProps>) {
  const tHeader = useTranslations('components.layout.header')
  const tDiscovery = useTranslations('discovery')

  return (
    <header className="fixed top-0 left-0 flex justify-between items-center w-full h-20 px-3 sm:px-6 z-50 bg-white/85 dark:bg-stone-950/85 backdrop-blur-2xl shadow-[0_4px_24px_rgba(0,0,0,0.04)] font-['Inter'] font-medium border-b border-stone-200 dark:border-stone-800 lg:px-12">
      <div className="flex items-center gap-2 sm:gap-4 lg:gap-10 flex-1 min-w-0">
        <LocalizedLink to="/" className="flex items-center gap-2 font-bold text-xl tracking-tight cursor-pointer active:scale-95 transition-all shrink-0">
          <img src="/logo.png" alt="Logo" className="w-10 h-10 object-contain" />
          <span className="hidden lg:block text-stone-900 dark:text-stone-50">{tHeader('brandName')}</span>
        </LocalizedLink>

        <PublicNavLinks
          className="hidden lg:flex items-center gap-4 text-sm font-semibold"
          linkClassName="rounded-full px-3 py-2 text-stone-600 transition-colors hover:bg-stone-100 hover:text-primary dark:text-stone-300 dark:hover:bg-stone-800"
        />

        <div className="hidden sm:flex flex-1 relative items-center lg:hidden">
          <GlobalSearchBar />
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 ml-2 sm:ml-4">
        <button
          type="button"
          aria-label={tDiscovery('searchPlaceholder')}
          className="flex h-11 min-w-11 items-center justify-center rounded-full text-stone-500 transition-colors hover:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-stone-400 dark:hover:bg-stone-800 sm:hidden"
          onClick={onSearchClick}
          data-testid="mobile-search-button"
        >
          <Search className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label={tDiscovery('mobileFilters.open')}
          className="relative flex h-11 min-w-11 items-center justify-center rounded-full text-stone-500 transition-colors hover:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-stone-400 dark:hover:bg-stone-800 lg:hidden"
          onClick={onFilterClick}
          data-testid="mobile-filter-button"
        >
          <SlidersHorizontal className="w-6 h-6" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-primary px-1 text-[10px] font-bold text-white dark:border-stone-950">
              {activeFilterCount}
            </span>
          )}
        </button>
        <div className="hidden sm:block">
          <ModeToggle />
        </div>
        <LocaleSwitcher />
        <MobilePublicNavMenu className="lg:hidden" />
        <PublicHeaderActions className="hidden lg:flex items-center gap-3" />
      </div>
    </header>
  )
}
